import {
  Badge,
  Box,
  FormLabel,
  HStack,
  Input,
  Link,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { translateError } from "@/common/utils/errorHandling";
import { useFlagsmithExportMutation } from "@/integration/data/useFlagsmithExportMutation";
import {
  flagsmithExportSchema,
  FlagsmithExportSchemaType,
} from "@reflag/shared/importAPI";

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ExportFlagsmithForm() {
  const form = useForm<FlagsmithExportSchemaType>({
    resolver: zodResolver(flagsmithExportSchema),
    mode: "onChange",
    defaultValues: {
      flagsmithApiToken: "",
    },
  });
  const exportMutation = useFlagsmithExportMutation();
  const errorToast = useErrorToast();
  const toast = useToast();

  const handleExport = async (data: FlagsmithExportSchemaType) => {
    try {
      const download = await exportMutation.mutateAsync(data);
      downloadBlob(download.blob, download.filename);
      form.reset();
      exportMutation.reset();
      toast({
        title: "Flagsmith export ready",
        description: "The JSON export has been downloaded.",
        status: "success",
        duration: 5000,
      });
    } catch (error) {
      const { title, description } = translateError(
        error,
        "Failed to export Flagsmith data",
      );
      errorToast({ title, description });
    }
  };

  return (
    <Box>
      <HStack pb={1}>
        <FormLabel m={0}>Flagsmith Export</FormLabel>
        <Badge colorScheme="purple" variant="subtle">
          Beta
        </Badge>
      </HStack>
      <Text color="dimmed" fontSize="sm" mb={4}>
        Download the current flags, environments, segments, and overrides for
        every Flagsmith project accessible to the API key.
      </Text>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleExport)}>
          <VStack align="flex-start" spacing={4}>
            <ManagedFormControl
              label="Flagsmith Management API Key"
              name="flagsmithApiToken"
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    autoComplete="off"
                    size="md"
                    type="password"
                  />
                  <Text color="dimmed" fontSize="sm">
                    Create a temporary key under Flagsmith organization settings
                    in the{" "}
                    <Link
                      href="https://docs.flagsmith.com/integrating-with-flagsmith/flagsmith-api-overview/admin-api/authentication"
                      target="_blank"
                      isExternal
                    >
                      API Access section
                    </Link>
                    . The key is used only for this export and is not persisted
                    by Reflag. Revoke the temporary key after downloading.
                  </Text>
                </>
              )}
            />
            <FormRootError />
            <FormSubmit
              isLoading={exportMutation.isPending}
              loadingText="Preparing export..."
            >
              Download JSON export
            </FormSubmit>
          </VStack>
        </form>
      </FormProvider>
    </Box>
  );
}
